import { MatcherCheckboxOption } from './types';

// Every multi-select checkbox step's options array lives here, data-only, rather than on the
// step component's own file. This file has no dependency on the context module, so flow/*.ts
// (buildStepQueue, buildSearchTerms) can depend on these arrays without ever routing back
// through a step component into the context that depends on flow/*.ts in the first place. Step
// components import their own array back from here to render it.

const CRISIS_RESOLUTION_CODES = [
  'AUDIT_REPRESENTATION',
  'BACK_TAX_UNFILED',
  'DEBT_NEGOTIATION_OIC',
  'DEBT_NEGOTIATION_INSTALLMENT',
  'PENALTY_ABATEMENT',
  'LIEN_LEVY_RELEASE',
  'WAGE_GARNISHMENT',
  'INNOCENT_SPOUSE',
  'INJURED_SPOUSE_RELIEF',
  'CNC_HARDSHIP_STATUS',
  'FOIA_REQUEST_ASSISTANCE',
  'STATUTE_OF_LIMITATIONS_DEFENSE',
  'PASSPORT_REVOCATION_RESOLUTION',
  'CRIMINAL_TAX_DEFENSE',
  'TAX_COURT_REPRESENTATION',
  'IRS_PPS_ACCESS'
];

// Every option here must send at least one term: the match search returns pros within the radius
// OR carrying a requested tag, so a fan whose answers send none can get no match at all.
export const SERVICES_OPTIONS: MatcherCheckboxOption[] = [
  {
    id: 'fileMyTaxes',
    label: 'File my taxes',
    // File is pulled from the flow for now, not deleted - its step/component/options still
    // exist, just unreferenced here until it's added back.
    followUpSteps: [],
    termValues: [{ category: 'serviceCapability.services', code: 'FILING' }]
  },
  {
    id: 'helpWithMyBusiness',
    label: 'Help with my business',
    // Industry and Entity are pulled from the flow for now, not deleted - same as File above.
    // Business stays.
    followUpSteps: ['business'],
    termValues: [{ category: 'whoTheyServe.filerType', code: 'SMALL_BUSINESS' }]
  },
  {
    id: 'resolveATaxProblem',
    label: 'Resolve a tax problem',
    // Problem is pulled from the flow for now, not deleted - same as File above.
    followUpSteps: [],
    termValues: CRISIS_RESOLUTION_CODES.map((code) => ({
      category: 'problemSituation.crisisResolution',
      code
    }))
  },
  {
    id: 'planForUpcomingTaxes',
    label: 'Plan for upcoming taxes',
    // Plan is pulled from the flow for now, not deleted - same as File above.
    followUpSteps: [],
    termValues: [
      { category: 'serviceCapability.services', code: 'TAX_STRATEGY' },
      { category: 'serviceCapability.services', code: 'ESTIMATED_PAYMENTS' }
    ]
  },
  {
    id: 'somethingElse',
    label: 'Something else',
    // Question is pulled from the flow for now, not deleted - same as File above. Deliberately
    // excludes tags the other Services options and the Business step send.
    followUpSteps: [],
    termValues: [
      { category: 'whoTheyServe.filerType', code: 'INDIVIDUAL_FILER' },
      { category: 'whoTheyServe.filerType', code: 'SELF_EMPLOYED' },
      { category: 'serviceCapability.services', code: 'QUICKBOOKS_SUPPORT' },
      {
        category: 'serviceCapability.services',
        code: 'GENERAL_ACCOUNTING_ATTESTATION'
      },
      { category: 'serviceCapability.services', code: 'FRACTIONAL_CFO' },
      { category: 'serviceCapability.services', code: 'BUSINESS_ADVISORY' },
      { category: 'whoTheyServe.situationModifiers', code: 'MULTI_STATE' },
      { category: 'whoTheyServe.situationModifiers', code: 'RETIREE' },
      {
        category: 'whoTheyServe.situationModifiers',
        code: 'REAL_ESTATE_INVESTOR'
      }
    ]
  }
];

export const BUSINESS_OPTIONS: MatcherCheckboxOption[] = [
  {
    id: 'businessTaxReturn',
    label: 'Business tax return',
    followUpSteps: [],
    termValues: [{ category: 'whoTheyServe.filerType', code: 'SMALL_BUSINESS' }]
  },
  {
    id: 'bookkeeping',
    label: 'Bookkeeping',
    followUpSteps: [],
    termValues: [
      { category: 'serviceCapability.services', code: 'BOOKKEEPING' }
    ]
  },
  {
    id: 'payroll',
    label: 'Payroll',
    followUpSteps: [],
    termValues: [{ category: 'serviceCapability.services', code: 'PAYROLL' }]
  },
  {
    id: 'quarterlyEstimatedTaxes',
    label: 'Quarterly estimated taxes',
    followUpSteps: [],
    termValues: [
      { category: 'serviceCapability.services', code: 'ESTIMATED_PAYMENTS' }
    ]
  },
  {
    id: 'businessSetup',
    label: 'Business setup',
    followUpSteps: [],
    termValues: [
      { category: 'serviceCapability.services', code: 'ENTITY_FORMATION' }
    ]
  },
  {
    id: 'notSureYet',
    label: 'Not sure yet',
    followUpSteps: [],
    termValues: []
  }
];

export const ENTITY_OPTIONS: MatcherCheckboxOption[] = [
  {
    id: 'soleProprietor',
    label: 'Just me (sole proprietor, LLC)',
    followUpSteps: [],
    termValues: [
      { category: 'serviceCapability.specificForms', code: 'SCHEDULE_C' }
    ]
  },
  {
    id: 'partnership',
    label: 'Partnership (LLC)',
    followUpSteps: [],
    termValues: [
      { category: 'serviceCapability.specificForms', code: 'FORM_1065' }
    ]
  },
  {
    id: 'sCorp',
    label: 'S-corp',
    followUpSteps: [],
    termValues: [
      { category: 'serviceCapability.specificForms', code: 'FORM_1120S' }
    ]
  },
  {
    id: 'cCorp',
    label: 'C-corp',
    followUpSteps: [],
    termValues: [
      { category: 'serviceCapability.specificForms', code: 'FORM_1120' }
    ]
  },
  {
    id: 'nonprofit',
    label: 'Nonprofit',
    followUpSteps: [],
    termValues: [
      { category: 'serviceCapability.specificForms', code: 'FORM_990' }
    ]
  },
  { id: 'notSure', label: 'Not sure', followUpSteps: [], termValues: [] }
];

export const PROBLEM_OPTIONS: MatcherCheckboxOption[] = [
  {
    id: 'oweBackTaxes',
    label: 'Owe back taxes',
    followUpSteps: [],
    termValues: [
      {
        category: 'problemSituation.crisisResolution',
        code: 'BACK_TAX_UNFILED'
      }
    ]
  },
  {
    id: 'needToFileAmendedReturn',
    label: 'Need to file amended return',
    followUpSteps: [],
    termValues: [
      { category: 'serviceCapability.services', code: 'AMENDED_RETURNS' }
    ]
  },
  {
    id: 'receivedNoticeFromIrs',
    label: 'Received a notice from IRS',
    followUpSteps: [],
    termValues: [
      {
        category: 'problemSituation.crisisResolution',
        code: 'AUDIT_REPRESENTATION'
      }
    ]
  },
  {
    id: 'beingAudited',
    label: 'Being audited',
    followUpSteps: [],
    termValues: [
      {
        category: 'problemSituation.crisisResolution',
        code: 'AUDIT_REPRESENTATION'
      }
    ]
  },
  {
    id: 'somethingElse',
    label: 'Something else',
    followUpSteps: [],
    termValues: []
  }
];

export const PLAN_OPTIONS: MatcherCheckboxOption[] = [
  {
    id: 'loweringNextYearsTaxBill',
    label: "Lowering next year's tax bill",
    followUpSteps: [],
    termValues: [
      { category: 'serviceCapability.services', code: 'TAX_STRATEGY' }
    ]
  },
  {
    id: 'sellingAPropertyOrBusiness',
    label: 'Selling a property or business',
    followUpSteps: [],
    termValues: [
      { category: 'problemSituation.growthTransition', code: 'BUSINESS_SALE' }
    ]
  },
  {
    id: 'movingRetirementOrInvestment',
    label: 'Moving retirement or investment',
    followUpSteps: [],
    termValues: [
      { category: 'serviceCapability.services', code: 'RETIREMENT_PLAN_ADMIN' }
    ]
  },
  {
    id: 'structuringBusinessOrEntityPlan',
    label: 'Structuring business or entity plan',
    followUpSteps: [],
    termValues: [
      {
        category: 'problemSituation.growthTransition',
        code: 'ENTITY_CONVERSION'
      }
    ]
  },
  {
    id: 'planningEstateOrGift',
    label: 'Planning estate or gift',
    followUpSteps: [],
    termValues: [
      { category: 'whoTheyServe.situationModifiers', code: 'ESTATE_TRUST' }
    ]
  },
  {
    id: 'somethingElse',
    label: 'Something else',
    followUpSteps: [],
    termValues: []
  }
];

// termValues are deliberately [] on every option here - rt-domain-pro-service's AI-tag
// vocabulary (see MatchingSearchTest) has no field for contact-channel preference, so this
// question never contributes to buildSearchTerms.
export const MEETING_OPTIONS: MatcherCheckboxOption[] = [
  {
    id: 'virtually',
    label: 'Virtually',
    followUpSteps: [],
    termValues: []
  },
  { id: 'inPerson', label: 'In person', followUpSteps: [], termValues: [] }
];

// No confirmed rt-domain-pro-service AI-tag mapping exists yet for sideGigIncome/
// retirementIncome/somethingElse - termValues stays [] on those until that mapping is defined,
// same as "Something else"/"Not sure" elsewhere in this file.
export const FILE_OPTIONS: MatcherCheckboxOption[] = [
  {
    id: 'sideGigIncome',
    label: 'Side-gig income',
    followUpSteps: [],
    termValues: []
  },
  {
    id: 'retirementIncome',
    label: 'Retirement income',
    followUpSteps: [],
    termValues: []
  },
  {
    id: 'rentalProperty',
    label: 'Rental property',
    followUpSteps: [],
    termValues: [
      { category: 'serviceCapability.specificForms', code: 'SCHEDULE_E' }
    ]
  },
  {
    id: 'inheritance',
    label: 'Inheritance',
    followUpSteps: [],
    termValues: [
      { category: 'problemSituation.lifeEventTriggers', code: 'INHERITANCE' }
    ]
  },
  {
    id: 'estateOrTrust',
    label: 'Estate or trust',
    followUpSteps: [],
    termValues: [
      { category: 'whoTheyServe.situationModifiers', code: 'ESTATE_TRUST' }
    ]
  },
  {
    id: 'somethingElse',
    label: 'Something else',
    followUpSteps: [],
    termValues: []
  }
];
